/* ==========================================================================
   Bundle deals — in-theme volume-break widget (replaces Kaching Bundles).
   Impact build.

   What this file does:
     - presents the quantity tiers and keeps their prices in sync with the
       variant the shopper has selected;
     - for multi-unit tiers it renders one variant picker per unit, so the
       shopper can mix colours/sizes inside a bundle;
     - on Add to cart it posts every chosen unit to /cart/add.js in a single
       request and then hands control back to Impact's cart drawer, using the
       same section-bundling handshake Impact's own <product-form> uses.

   What this file does NOT do: apply the discount. Money is discounted by a
   Shopify AUTOMATIC DISCOUNT (e.g. "2+ items -> 10% off"). The percentages
   configured on the block are display only and must match those discounts.

   THEME COUPLING (all of it lives in the ThemeBridge object below):
     - money format          window.themeVariables.settings.moneyFormat
     - selected variant      form.id.value  (the <input name="id"> element)
     - option inputs         input[data-option-position][form="<form id>"],
                             whose value is the option VALUE ID, not its name
     - variant changed       'variant:change' dispatched on the form
     - after add to cart     'cart:prepare-bundled-sections' -> add.js with
                             sections -> 'variant:add' + 'cart:change'
   ========================================================================== */

(function () {
  'use strict';

  var SELECTED = 'is-selected';

  /* ------------------------------------------------------------- theme bridge */

  var ThemeBridge = {
    root: function () {
      return (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    },

    moneyFormat: function () {
      var vars = window.themeVariables;
      var format = (vars && vars.settings && vars.settings.moneyFormat) || '${{amount}}';
      // Some shops wrap the amount in <span class="money"> for currency
      // converters. We write the result with textContent, so the markup would
      // show up literally — strip it.
      return format.replace(/<[^>]*>/g, '');
    },

    /* Shopify's own money filter, in the four formats a shop can be set to. */
    formatMoney: function (cents) {
      var format = ThemeBridge.moneyFormat();
      var value = Number(cents) || 0;

      function withDelimiters(number, precision, thousands, decimal) {
        number = (number / 100.0).toFixed(precision);
        var parts = number.split('.');
        var whole = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
        var fraction = parts[1] ? decimal + parts[1] : '';
        return whole + fraction;
      }

      return format.replace(/\{\{\s*(\w+)\s*\}\}/, function (_match, name) {
        switch (name) {
          case 'amount':
            return withDelimiters(value, 2, ',', '.');
          case 'amount_no_decimals':
            return withDelimiters(value, 0, ',', '.');
          case 'amount_with_comma_separator':
            return withDelimiters(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return withDelimiters(value, 0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return withDelimiters(value, 2, "'", '.');
          case 'amount_no_decimals_with_space_separator':
            return withDelimiters(value, 0, ' ', ',');
          case 'amount_with_space_separator':
            return withDelimiters(value, 2, ' ', ',');
          case 'amount_with_period_and_space_separator':
            return withDelimiters(value, 2, ' ', '.');
          default:
            return withDelimiters(value, 2, ',', '.');
        }
      });
    },

    /* The variant the theme currently has selected, as an id. */
    selectedVariantId: function (form) {
      if (!form) return 0;
      var input = form.elements ? form.elements.namedItem('id') : null;
      return input ? Number(input.value) : 0;
    },

    /* Drive Impact's own variant picker so the gallery, price and URL follow. */
    selectOptionValues: function (form, valueIds) {
      if (!form) return;

      valueIds.forEach(function (valueId, index) {
        if (!valueId) return;

        var selector =
          'input[data-option-position="' + (index + 1) + '"][form="' + form.id + '"]' +
          '[value="' + valueId + '"]';
        var input = document.querySelector(selector);

        if (!input || input.checked) return;
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    },

    /* Mirror <product-form>._onSubmit so the drawer updates exactly as usual. */
    addLines: function (form, items) {
      var sections = ['variant-added'];

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:prepare-bundled-sections', {
          bubbles: true,
          detail: { sections: sections }
        })
      );

      var root = ThemeBridge.root();
      var variantId = ThemeBridge.selectedVariantId(form) || items[0].id;

      return fetch(root + 'cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify({
          items: items,
          sections: sections.join(','),
          sections_url: root + 'variants/' + variantId
        })
      }).then(function (response) {
        return response.json().then(function (json) {
          return { ok: response.ok, json: json };
        });
      });
    },

    /* Tell the theme the cart changed, so the drawer redraws and opens. */
    announceAdd: function (form, response) {
      var root = ThemeBridge.root();

      return fetch(root + 'cart.js', { credentials: 'same-origin' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          cart.sections = response.sections;

          var detail = {
            items: response.items || [response],
            cart: cart
          };

          if (form) {
            form.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: detail }));
          } else {
            document.dispatchEvent(new CustomEvent('variant:add', { bubbles: true, detail: detail }));
          }

          document.documentElement.dispatchEvent(
            new CustomEvent('cart:change', {
              bubbles: true,
              detail: { baseEvent: 'variant:add', cart: cart }
            })
          );
        });
    }
  };

  /* ------------------------------------------------------------- the widget */

  class BundleDeals extends HTMLElement {
    connectedCallback() {
      var configEl = this.querySelector('[data-bundle-config]');
      if (!configEl) return;

      try {
        this.config = JSON.parse(configEl.textContent);
      } catch (e) {
        return;
      }

      this.product = this.config.product;
      if (!this.product || !this.product.variants.length) return;

      this.form = document.forms[this.dataset.formId] || document.getElementById(this.dataset.formId);
      this.tiers = Array.prototype.slice.call(this.querySelectorAll('[data-tier]'));
      this.errorEl = this.querySelector('[data-bd-error]');

      this.currentVariant = this.getCurrentVariant();
      this.selectedTier = this.tiers.find(function (t) {
        return t.classList.contains(SELECTED);
      }) || this.tiers[0];

      this.buildUnitPickers();
      this.bindEvents();
      this.selectTier(this.selectedTier, true);
    }

    /* ---------------------------------------------------------------- data */

    getCurrentVariant() {
      var id = ThemeBridge.selectedVariantId(this.form);
      return this.findVariantById(id) || this.product.variants.find(function (v) {
        return v.available;
      }) || this.product.variants[0];
    }

    findVariantById(id) {
      if (!id) return null;
      return this.product.variants.find(function (v) { return v.id === id; }) || null;
    }

    findVariantByOptions(values) {
      return this.product.variants.find(function (v) {
        return v.options.every(function (o, i) { return o === values[i]; });
      }) || null;
    }

    /* Option VALUE ids for a variant, in option order — Impact's radios key
       off these ids rather than the value names. */
    optionValueIds(variant) {
      var options = this.product.options;
      return variant.options.map(function (name, i) {
        var value = (options[i] && options[i].values || []).find(function (v) {
          return v.name === name;
        });
        return value ? value.id : null;
      });
    }

    /* ------------------------------------------------------------ rendering */

    buildUnitPickers() {
      if (!this.config.differentVariants) return;
      if (this.product.options.length === 0) return;

      var self = this;

      this.tiers.forEach(function (tier) {
        var qty = Number(tier.dataset.qty);
        var holder = tier.querySelector('[data-bd-units]');
        if (!holder || qty < 2) return;

        holder.classList.add('is-enabled');
        holder.innerHTML = '';

        for (var i = 0; i < qty; i++) {
          holder.appendChild(self.buildUnitRow(i));
        }
      });
    }

    buildUnitRow(index) {
      var self = this;
      var row = document.createElement('div');
      row.className = 'bundle-deals__unit';
      row.dataset.unit = String(index);

      var label = document.createElement('span');
      label.className = 'bundle-deals__unit-label';
      label.textContent = this.config.strings.item.replace('[number]', index + 1);
      row.appendChild(label);

      this.product.options.forEach(function (option, optionIndex) {
        var select = document.createElement('select');
        select.className = 'bundle-deals__select';
        select.dataset.optionIndex = String(optionIndex);
        select.setAttribute('aria-label', option.name);

        option.values.forEach(function (value) {
          var opt = document.createElement('option');
          opt.value = value.name;
          opt.textContent = value.name;
          if (self.currentVariant.options[optionIndex] === value.name) opt.selected = true;
          select.appendChild(opt);
        });

        select.addEventListener('change', function () {
          self.onUnitChange(row);
        });

        row.appendChild(select);
      });

      return row;
    }

    /* Values of a unit row, as an array in option order. */
    unitValues(row) {
      return Array.prototype.map.call(
        row.querySelectorAll('.bundle-deals__select'),
        function (s) { return s.value; }
      );
    }

    unitVariant(row) {
      return this.findVariantByOptions(this.unitValues(row));
    }

    /* Grey out combinations that do not exist or are sold out. */
    refreshUnitAvailability(row) {
      var self = this;
      var values = this.unitValues(row);

      row.querySelectorAll('.bundle-deals__select').forEach(function (select) {
        var idx = Number(select.dataset.optionIndex);

        Array.prototype.forEach.call(select.options, function (opt) {
          var probe = values.slice();
          probe[idx] = opt.value;
          var variant = self.findVariantByOptions(probe);
          var ok = !!(variant && variant.available);
          opt.disabled = !ok;
          opt.textContent = ok ? opt.value : opt.value + ' — ' + self.config.strings.unavailable;
        });

        var variant = self.unitVariant(row);
        select.dataset.unavailable = variant && variant.available ? 'false' : 'true';
      });
    }

    /* ------------------------------------------------------------- pricing */

    /* Variants making up a tier: per-unit choices when available,
       otherwise the page's current variant repeated. */
    tierVariants(tier) {
      var self = this;
      var qty = Number(tier.dataset.qty);
      var rows = tier.querySelectorAll('[data-unit]');

      if (!rows.length) {
        return new Array(qty).fill(this.currentVariant);
      }

      return Array.prototype.map.call(rows, function (row) {
        return self.unitVariant(row) || self.currentVariant;
      });
    }

    updatePrices() {
      var self = this;

      this.tiers.forEach(function (tier) {
        var discount = Number(tier.dataset.discount) || 0;
        var variants = self.tierVariants(tier);

        var full = 0;   // compare-at total, for the struck-through price
        var gross = 0;  // today's price before the bundle discount
        var net = 0;    // what they actually pay

        variants.forEach(function (variant) {
          var price = variant.price;
          var compare = self.config.useCompareAt && variant.compare_at_price > price
            ? variant.compare_at_price
            : price;
          full += compare;
          gross += price;
        });

        net = Math.round(gross * (100 - discount) / 100);

        var priceEl = tier.querySelector('[data-bd-price]');
        var compareEl = tier.querySelector('[data-bd-compare]');
        var labelEl = tier.querySelector('[data-bd-label]');

        if (priceEl) priceEl.textContent = ThemeBridge.formatMoney(net);

        if (compareEl) {
          var showCompare = full > net;
          compareEl.textContent = showCompare ? ThemeBridge.formatMoney(full) : '';
          compareEl.style.display = showCompare ? '' : 'none';
        }

        if (labelEl) {
          // 'extra' counts only what the bundle discount saves, so a single
          // item shows nothing. 'total' also counts the compare-at saving,
          // which a shopper gets whether they bundle or not.
          var saved = self.config.savingsBasis === 'total' ? full - net : gross - net;
          labelEl.textContent = labelEl.dataset.template.replace(
            '[saved_total]',
            ThemeBridge.formatMoney(saved > 0 ? saved : 0)
          );
          labelEl.style.display = saved > 0 ? '' : 'none';
        }
      });
    }

    /* -------------------------------------------------------------- events */

    bindEvents() {
      var self = this;

      this.tiers.forEach(function (tier) {
        tier.addEventListener('click', function (event) {
          if (event.target.closest('[data-bd-units]')) return; // don't steal picker clicks
          self.selectTier(tier);
        });
      });

      // Impact re-renders the product info on every variant change and fires
      // this on the form. Our block survives the partial re-render, so we only
      // have to re-read the variant.
      document.addEventListener('variant:change', function (event) {
        var variant = event.detail && event.detail.variant;
        if (!variant) return;
        var full = self.findVariantById(variant.id);
        if (!full) return;
        self.currentVariant = full;
        self.syncFirstUnit();
        self.updatePrices();
      });

      // Add to cart is intercepted in the CAPTURE phase on the document, so we
      // run before <product-form>'s own submit handler (and before any cart
      // drawer app that binds to the button instead of the form).
      document.addEventListener('submit', function (event) {
        if (event.target !== self.form) return;
        if (!self.wantsBundle()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        self.submitBundle();
      }, true);

      // The sticky bar and the main buy buttons both submit the same form, but
      // an app that hijacks the click never lets the submit fire. Cover both.
      document.addEventListener('click', function (event) {
        var button = event.target.closest && event.target.closest('button[type="submit"], input[type="submit"]');
        if (!button) return;

        var owner = button.form || document.forms[button.getAttribute('form')];
        if (!owner || owner !== self.form) return;
        if (!self.wantsBundle()) return;

        event.preventDefault();
        event.stopImmediatePropagation();
        self.submitBundle();
      }, true);
    }

    /* Keep unit 1 in step with the theme's own variant picker. */
    syncFirstUnit() {
      var self = this;

      this.tiers.forEach(function (tier) {
        var row = tier.querySelector('[data-unit="0"]');
        if (!row) return;

        row.querySelectorAll('.bundle-deals__select').forEach(function (select) {
          var idx = Number(select.dataset.optionIndex);
          select.value = self.currentVariant.options[idx];
        });

        self.refreshUnitAvailability(row);
      });
    }

    onUnitChange(row) {
      this.refreshUnitAvailability(row);

      // Unit 1 drives the theme's picker so the gallery and price follow along.
      if (row.dataset.unit === '0' && !this.config.disableSync) {
        var variant = this.unitVariant(row);
        if (variant) ThemeBridge.selectOptionValues(this.form, this.optionValueIds(variant));
      }

      this.clearError();
      this.updatePrices();
    }

    selectTier(tier, silent) {
      var self = this;

      this.tiers.forEach(function (t) {
        var on = t === tier;
        t.classList.toggle(SELECTED, on);
        var radio = t.querySelector('input[type="radio"]');
        if (radio) radio.checked = on;
      });

      this.selectedTier = tier;
      this.clearError();

      tier.querySelectorAll('[data-unit]').forEach(function (row) {
        self.refreshUnitAvailability(row);
      });

      this.updatePrices();

      if (!silent) {
        this.dispatchEvent(new CustomEvent('bundle:tierchange', {
          bubbles: true,
          detail: { quantity: Number(tier.dataset.qty), discount: Number(tier.dataset.discount) }
        }));
      }
    }

    /* ---------------------------------------------------------------- cart */

    /* A single-item tier is left to the theme's own add to cart. */
    wantsBundle() {
      var tier = this.selectedTier;
      return !!tier && Number(tier.dataset.qty) > 1 && !this.adding;
    }

    submitBundle() {
      var tier = this.selectedTier;
      if (!tier) return;

      var variants = this.tierVariants(tier);
      var unavailable = variants.some(function (v) { return !v || !v.available; });

      if (unavailable) {
        this.showError(this.config.strings.unavailableSelection);
        return;
      }

      // Collapse repeated variants into single lines with a quantity.
      var lines = [];
      variants.forEach(function (variant) {
        var existing = lines.find(function (l) { return l.id === variant.id; });
        if (existing) existing.quantity += 1;
        else lines.push({ id: variant.id, quantity: 1 });
      });

      this.addToCart(lines);
    }

    addToCart(items) {
      var self = this;
      var buttons = this.form
        ? Array.prototype.filter.call(this.form.elements, function (el) { return el.type === 'submit'; })
        : [];

      buttons.forEach(function (b) { b.setAttribute('aria-busy', 'true'); });
      this.adding = true;

      ThemeBridge.addLines(this.form, items)
        .then(function (result) {
          buttons.forEach(function (b) { b.removeAttribute('aria-busy'); });
          self.adding = false;

          if (!result.ok) {
            self.showError(result.json.description || result.json.message || self.config.strings.error);
            document.dispatchEvent(new CustomEvent('cart:refresh'));
            return;
          }

          return ThemeBridge.announceAdd(self.form, result.json);
        })
        .catch(function () {
          buttons.forEach(function (b) { b.removeAttribute('aria-busy'); });
          self.adding = false;
          self.showError(self.config.strings.error);
        });
    }

    /* --------------------------------------------------------------- errors */

    showError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message;
      this.errorEl.classList.add('is-visible');
    }

    clearError() {
      if (!this.errorEl) return;
      this.errorEl.classList.remove('is-visible');
    }
  }

  if (!customElements.get('bundle-deals')) {
    customElements.define('bundle-deals', BundleDeals);
  }
})();

/* ==========================================================================
   Bundle deals — countdown timer bar.

   Three modes, matching the Kaching settings they replace:
     midnight  - counts down to the shopper's own local midnight, then rolls
                 over to the next day (never expires)
     duration  - a fixed number of minutes, held for the browsing session
     date      - counts down to one fixed date, then hides itself
   ========================================================================== */

(function () {
  'use strict';

  function two(n) {
    return n < 10 ? '0' + n : String(n);
  }

  function formatLeft(ms) {
    var total = Math.max(0, Math.floor(ms / 1000));
    var h = Math.floor(total / 3600);
    var m = Math.floor((total % 3600) / 60);
    var s = total % 60;
    return h > 0 ? two(h) + ':' + two(m) + ':' + two(s) : two(m) + ':' + two(s);
  }

  function nextMidnight() {
    var d = new Date();
    d.setHours(24, 0, 0, 0);
    return d.getTime();
  }

  function targetFor(el) {
    var type = el.dataset.timerType;

    if (type === 'duration') {
      var minutes = Number(el.dataset.timerMinutes) || 15;
      var key = 'bundleDealsTimer';
      var stored = null;
      try {
        stored = window.sessionStorage.getItem(key);
      } catch (e) {
        stored = null;
      }
      var end = stored ? Number(stored) : Date.now() + minutes * 60000;
      if (!stored) {
        try {
          window.sessionStorage.setItem(key, String(end));
        } catch (e) {
          /* private mode: fall back to a per-page countdown */
        }
      }
      return end;
    }

    if (type === 'date') {
      var parsed = Date.parse(el.dataset.timerEnd);
      return isNaN(parsed) ? 0 : parsed;
    }

    return nextMidnight();
  }

  function start(el) {
    var template = el.dataset.timerTemplate || '';
    var target = targetFor(el);

    function tick() {
      var left = target - Date.now();

      if (left <= 0) {
        if (el.dataset.timerType === 'midnight') {
          target = nextMidnight();
          left = target - Date.now();
        } else {
          el.hidden = true;
          window.clearInterval(handle);
          return;
        }
      }

      var value = '<span class="bundle-deals__timer-value">' + formatLeft(left) + '</span>';
      el.innerHTML = template.split('[timer]').join(value);
      el.hidden = false;
    }

    var handle = window.setInterval(tick, 1000);
    tick();
  }

  function init() {
    var bars = document.querySelectorAll('[data-bd-timer]');
    Array.prototype.forEach.call(bars, function (el) {
      if (el.dataset.timerReady) return;
      el.dataset.timerReady = '1';
      start(el);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
